import {useMutation, useQuery} from '@apollo/client'
import {Box, Card, InputAdornment, Link, Tooltip, Typography} from '@mui/material'
import {makeStyles} from '@mui/styles'
import gql from 'graphql-tag'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {
  Create,
  Datagrid,
  EditContextProvider,
  FilterLiveSearch,
  FunctionField,
  maxLength,
  regex,
  required,
  SaveButton,
  SelectInput,
  Show,
  ShowButton,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  useEditController,
  useNotify,
  useRecordContext,
  useRedirect,
  useTranslate,
} from 'react-admin'
import {FieldValues, useFormContext, useWatch} from 'react-hook-form'

import BulkDeleteButton from '../components/BulkDeleteButton'
import {
  ChatMessageTemplateField,
} from '../components/chatMessageTemplate/ChatMessageTemplateEditor'
import {
  ApprovedIcon,
  ChevronRightIcon,
  ClockIcon,
  RejectedIcon,
  SearchIcon,
} from '../components/icons'
import SubtitledInput from '../components/SubtitledInput'
import {useCompileChatMessageText} from '../hooks/useCompileChatMessageText'
import useHasPermission from '../hooks/useHasPermission'
import useIntervalRefresh from '../hooks/useIntervalRefresh'
import useIsDesktop from '../hooks/useIsDesktop'
import useSoftDelete from '../hooks/useSoftDelete'
import {ChatMessageTemplates} from '../types/graphqlSchema'
import {EMOJI_REGEX} from '../utils/consts'
import DeleteWithConfirmButton from './components/DeleteWithConfirmButton'
import ExtendedList from './components/ExtendedList'
import ExtendedPagination from './components/ExtendedPagination'
import {CreateHeader, ShowHeader} from './components/ResourceHeader'
import TemplateBuilderButtons from './components/TemplateBuilderButtons'
import TemplateBuilderHeader from './components/TemplateBuilderHeader'
import TemplateBuilderTextToolbar from './components/TemplateBuilderTextToolbar'

const ChatMessageTemplatesList = props => {
  useIntervalRefresh(60000)
  const translate = useTranslate()
  const {data: {chat_message_templates: chatMessageTemplates = []} = {}} = useQuery(
    DISTINCT_CHAT_MESSAGE_TEMPLATE_LANGUAGES_QUERY,
    {fetchPolicy: 'cache-first'},
  )
  const languageChoices = useMemo(
    () => chatMessageTemplates.map(({language}) => ({
      id: language,
      name: translate(`languages.${language}`),
    })),
    [chatMessageTemplates, translate]
  )
  const filter = useMemo(() => ({isDeleted: false, isListed: true}), [])
  return (
    <ExtendedList
      {...props}
      description={(
        <Typography color="textSecondary">
          {translate('resources.chat_message_templates.description')}
        </Typography>
      )}
      disableSyncWithLocation={false}
      filter={filter}
      filters={[
        <FilterLiveSearch
          // @ts-ignore: 'InputProps' does not exist
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon color="secondary" />
              </InputAdornment>
            ),
            onKeyDown: e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                e.stopPropagation()
              }
            },
          }}
          alwaysOn
          key="fullText"
          source={[
            "headline@_ilike",
            "name@_ilike",
            "text@_ilike",
            "buttonText@_ilike",
          ].join(',')}
        />,
        <SelectInput
          choices={TYPES.map(type => ({
            id: type,
            name: translate(`resources.chat_message_templates.types.${type}`),
          }))}
          isRequired
          key="type"
          label={translate('resources.chat_message_templates.fields.category')}
          optionText="name"
          optionValue="id"
          source="type"
          sx={{'& span[aria-hidden="true"]': {display: 'none'}}}
        />,
        <SelectInput
          choices={STATUSES.map(status => ({
            id: status,
            name: translate(`whatsapp_message_templates.statusLabels.${status}`),
          }))}
          isRequired
          key="status"
          label={translate('resources.chat_message_templates.fields.status')}
          optionText="name"
          optionValue="id"
          source="status"
          sx={{'& span[aria-hidden="true"]': {display: 'none'}}}
        />,
        <SelectInput
          choices={languageChoices}
          isRequired
          key="language"
          label={translate('resources.chat_message_templates.fields.language')}
          optionText="name"
          optionValue="id"
          source="language"
          sx={{'& span[aria-hidden="true"]': {display: 'none'}}}
        />,
      ]}
      pagination={<ExtendedPagination rowsPerPageOptions={[]} />}
      perPage={25}
      sort={{field: 'insertionTimestamp', order: 'DESC'}}
    >
      <ChatMessageTemplateDatagrid />
    </ExtendedList>
  )
}

const ChatMessageTemplateDatagrid = () => {
  const styles = useStyles()
  const isDesktop = useIsDesktop()
  const redirect = useRedirect()
  const translate = useTranslate()
  const canDelete = useHasPermission('delete', 'chat_message_templates')
  return (
    <Datagrid
      bulkActionButtons={canDelete && <BulkDeleteButton shouldSoftDelete />}
      className={styles.table}
      rowClick={false}
    >
      <TextField source="name" variant="body1"/>
      <FunctionField
        render={() => <StatusIconField />} source="approvalStatus" sortBy="status"
      />
      {isDesktop && <FunctionField render={() => <TypeLabelField />} source="type"/>}
      {isDesktop && (
        <FunctionField<ChatMessageTemplates>
          render={
            ({id, status, type} = {} as ChatMessageTemplates) => (
              (type === 'MARKETING') && (status === 'APPROVED')
            ) && (
              <Link onClick={() => redirect(`/campaigns/create?templateId=${id}`)}>
                {translate('resources.chat_message_templates.startCampaign')}
              </Link>
            )
          }
        />
      )}
      <ShowButton
        icon={<ChevronRightIcon />}
        label=""
        // @ts-ignore
        source="showIconButton"
      />
    </Datagrid>
  )
}

const ChatMessageTemplateShow = () => {
  const translate = useTranslate()
  const editController = useEditController()
  const softDelete = useSoftDelete({
    ids: [editController.record?.id],
    onError: console.error,
  })
  return (
    <Show actions={<ChatMessageTemplatesShowHeader />}>
      <SimpleShowLayout>
        <TextField source="name" variant="body1" />
        <FunctionField
          render={r => translate(
            `resources.chat_message_templates.types.${r?.type}`, {_: ''}
          )}
          source="category"
          variant="body1"
        />
        <StatusDescriptionField addLabel source="status" />
        <ChatMessageTemplateField />
        {useHasPermission('delete', 'chat_message_templates') && (
          <Toolbar>
            <EditContextProvider value={editController}>
              <DeleteWithConfirmButton
                confirmContent={
                  translate('resources.chat_message_templates.confirmDeletion')
                }
                confirmTitle={record => record.name}
                onConfirmDelete={softDelete}
              />
            </EditContextProvider>
          </Toolbar>
        )}
      </SimpleShowLayout>
    </Show>
  )
}

const ChatMessageTemplatesShowHeader = () => (
  <ShowHeader title={useRecordContext()?.name} />
)

const ChatMessageTemplateCreate = () => {
  const styles = useStyles()
  const isDesktop = useIsDesktop()
  const [formData, setFormData] = useState()
  const redirect = useRedirect()
  const notify = useNotify()
  const translate = useTranslate()
  const [upsertChatMessageTemplate] = useMutation(CHAT_MESSAGE_TEMPLATE_UPSERT_MUTATION)
  const {placeholderTypeToLabel} = useCompileChatMessageText()
  const save = useCallback(
    async ({
      addHeaderMediaSwitch,
      chatMessageTemplateButtons,
      footer,
      headerMediaBase64,
      headerMediaFilename,
      headerMediaMimeType,
      headline,
      name,
      text,
      type,
    }: FieldValues) => {
      if (addHeaderMediaSwitch && !headerMediaBase64) {
        notify(
          'resources.chat_message_templates.create.imageRequiredForHeaderMediaSwitch',
          {type: 'error'},
        )
        return
      }
      const placeholderTranslatedText = Object.entries(placeholderTypeToLabel)
        .reduce(
          (translatedText, [placeholderType, placeholderLabel]) => (
            translatedText?.replaceAll(
              `{{%${placeholderLabel}%}}`,
              `{{%${placeholderType}%}}`,
            )
          ),
          text,
        )
      chatMessageTemplateButtons?.forEach(b => {
        // Suffixing the URL with "{{1}}" is required for dynamic template button URL.
        if (b.urlType === 'DYNAMIC') b['url'] = `${b['url']}{{1}}`
      })
      try {
        await upsertChatMessageTemplate({
          variables: {
            chatMessageTemplateButtons,
            footer,
            headerMediaBase64,
            headerMediaFilename,
            headerMediaMimeType,
            headline,
            name: name.trim(),
            text: placeholderTranslatedText,
            type,
          },
        })
        notify('resources.chat_message_templates.create.success')
        redirect('list', '/chat_message_templates')
      }
      catch (e: any) {
        const errorCode = e.graphQLErrors?.[0]?.extensions[0]?.code
        const message =
          translate(
            `resources.chat_message_templates.errorMessages.${errorCode}`,
            (errorCode === 'VIDEO_HEADER_MEDIA_CODEC_NOT_SUPPORTED' ?
              {codec: e.graphQLErrors?.[0]?.extensions[0]?.data?.current_codec} : {_: ''}
            )
          )
        notify(message, {type: 'error'})
      }
    },
    [
      notify,
      placeholderTypeToLabel,
      redirect,
      translate,
      upsertChatMessageTemplate,
    ]
  )
  return (
    <Create
      className={styles.create}
      actions={<CreateHeader />}
      aside={isDesktop ? <CreateAside formData={formData} /> : undefined}
      redirect="list"
    >
      <SimpleForm
        onSubmit={save}
        toolbar={<ChatMessageTemplateCreateToolbar />}
        mode="all"
        reValidateMode="onBlur"
      >
        <FormSpy onChange={setFormData} />
        <Card elevation={2} className={styles.createCard}>
          <SubtitledInput
            isEmptyValidationErrorMessageHidden
            subtitle={translate('resources.chat_message_templates.create.nameSubtitle')}
            title={translate('resources.chat_message_templates.fields.name')}
          >
            <TextInput
              source="name"
              label={
                translate('resources.chat_message_templates.create.inputs.name.label')
              }
              validate={required()}
            />
          </SubtitledInput>
          <SubtitledInput
            isEmptyValidationErrorMessageHidden
            subtitle={translate('resources.chat_message_templates.create.typeSubtitle')}
            title={translate('resources.chat_message_templates.fields.type')}
          >
            <SelectInput
              choices={
                TYPES
                  .map((type, index) => ({
                    id: index,
                    key: type,
                    name: translate(`resources.chat_message_templates.types.${type}`),
                  }))
                  .sort((a, b) => (a.name > b.name) ? 1 : ((a.name < b.name) ? -1 : 0))
              }
              defaultValue={DEFAULT_TEMPLATE_TYPE}
              label=""
              optionValue="key"
              source="type"
              validate={required()}
            />
          </SubtitledInput>
        </Card>
        <CreateContentCard />
        <CreateButtonCard />
        {!isDesktop && (
          <Card elevation={2} style={{width: '100%'}}>
            <Typography className={styles.createTemplatePreviewTitle}>
              {translate('resources.chat_message_templates.create.templatePreviewTitle')}
            </Typography>
            <ChatMessageTemplateField
              isPropertiesPaneVisible={false}
              templateFormData={formData}
            />
          </Card>
        )}
      </SimpleForm>
    </Create>
  )
}

const FormSpy = ({onChange}) => {
  const formData = useWatch()
  useEffect(() => {onChange(formData)}, [formData, onChange])
  return null
}

const CreateAside = ({formData}) => {
  const styles = useStyles()
  return (
    <Card elevation={2} className={styles.createAside}>
      <Typography className={styles.createTemplatePreviewTitle}>
        {useTranslate()('resources.chat_message_templates.create.templatePreviewTitle')}
      </Typography>
      <ChatMessageTemplateField
        isPropertiesPaneVisible={false}
        templateFormData={formData}
      />
    </Card>
  )
}

const CreateContentCard = () => {
  const styles = useStyles()
  return (
    <Card elevation={2} className={styles.createCard}>
      <Box>
        <Typography variant="subtitle2" component="p" sx={{marginBottom: '0.5rem'}}>
          {useTranslate()(
            'resources.chat_message_templates.create.contentCard.title'
          )}
        </Typography>
        <Typography color="textDisabled">
          {useTranslate()(
            'resources.chat_message_templates.create.contentCard.subtitle'
          )}
        </Typography>
      </Box>
      <TemplateBuilderHeader />
      <TemplateBuilderTextToolbar />
      <SubtitledInput
        isEmptyValidationErrorMessageHidden
        subtitle=""
        title={useTranslate()(
          'resources.chat_message_templates.create.inputs.footer.title'
        )}
      >
        <TextInput
          label={useTranslate()(
            'resources.chat_message_templates.create.inputs.footer.label'
          )}
          source="footer"
          validate={[
            maxLength(
              60,
              useTranslate()('validations.maxCharacters', {max: 60})
            ),
            regex(EMOJI_REGEX, useTranslate()('validations.emojisNotAllowed')),
          ]}
        />
      </SubtitledInput>
    </Card>
  )
}

const CreateButtonCard = () => {
  const styles = useStyles()
  return (
    <Card elevation={2} className={styles.createCard}>
      <TemplateBuilderButtons />
    </Card>
  )
}

const ChatMessageTemplateCreateToolbar = () => {
  const {formState: {isValid}} = useFormContext()
  return (
    <Toolbar>
      <SaveButton
        disabled={!isValid}
        label={useTranslate()('resources.chat_message_templates.create.createButton')}
        type="submit"
      />
    </Toolbar>
  )
}

const StatusDescriptionField = props => {
  const styles = useStyles()
  const translate = useTranslate()
  const {status} = useRecordContext<ChatMessageTemplates>() ?? {}
  return (
    <span className={styles.statusDescriptionField}>
      <StatusIconField {...props} />
      <Typography variant="body1">
        {translate(`whatsapp_message_templates.statusLabels.${status}`)}:{' '}
        {translate(`whatsapp_message_templates.statusDescriptions.${status}`)}
      </Typography>
    </span>
  )
}

const TypeLabelField = () => (
  <Typography variant="body1">
    {useTranslate()(`resources.chat_message_templates.types.${useRecordContext()?.type}`)}
  </Typography>
)

const StatusIconField = () => {
  const {rejectionReason, status} = useRecordContext<ChatMessageTemplates>() ?? {}
  const translate = useTranslate()
  const icon = (() => {
    switch (status) {
      case 'APPROVED': return <ApprovedIcon color="success" />
      case 'REJECTED': return <RejectedIcon color="error" />
      default: return <ClockIcon />
    }
  })()
  const title = (
    <Box display="flex" flexDirection="column" gap="0.5rem">
      <span>{translate(`whatsapp_message_templates.statusDescriptions.${status}`)}</span>
      {(status === 'REJECTED') && rejectionReason && (
        <>
          <span>"{rejectionReason}"</span>
          <Link
            href={
              "https://developers.facebook.com/documentation/business-messaging/" +
              "whatsapp/templates/template-review"
            }
            target="_blank"
          >
            {translate('actions.moreInfo')}
          </Link>
        </>
      )}
    </Box>
  )
  return (
    <Tooltip
      arrow
      title={title}
    >
      {/* TODO: remove span once our SvgIcon component handles forwardRef well */}
      <span>{icon}</span>
    </Tooltip>
  )
}

const useStyles = makeStyles(theme => ({
  create: {
    '& .MuiToolbar-root': {
      marginTop: theme.remSpacing(2),
      width: '100%',
    },
    maxWidth: '100%',
  },
  createAside: {
    marginLeft: theme.remSpacing(3),
    maxHeight: 'max-content',
    position: 'sticky',
    top: theme.remSpacing(2),
    width: '60%',
    [theme.breakpoints.down('xl')]: {
      width: '60% !important',
    },
  },
  createCard: {
    borderRadius: '4px',
    gap: theme.remSpacing(4),
    marginBottom: theme.remSpacing(2),
    padding: theme.remSpacing(3),
    width: '100%',
  },
  createTemplatePreviewTitle: {
    padding: theme.remSpacing(2),
  },
  statusDescriptionField: {
    '& .MuiSvgIcon-root': {
      marginRight: theme.remSpacing(1),
    },
    display: 'inline-flex',
  },
  table: {
    '& .RaDatagrid-table': {
      [theme.breakpoints.down('lg')]: {
        '& .column-name': {
          width: '50% !important',
        },
        '& .column-showIconButton': {
          width: theme.remSpacing(5),
        },
        '& td': {
          verticalAlign: 'middle',
        },
        marginBottom: theme.typography.pxToRem(50),
      },
      '& .column-name': {
        width: '40%',
      },
    },
  },
}))

// TODO: Load this list via graphql query from chat_message_template_types table
const TYPES = ['UTILITY', 'MARKETING'] // 'UTILITY' should come first!
const DEFAULT_TEMPLATE_TYPE = TYPES[0]
const STATUSES = ['PENDING', 'APPROVED', 'REJECTED', 'DELETED']

const CHAT_MESSAGE_TEMPLATE_UPSERT_MUTATION = gql`
  mutation(
    $chatMessageTemplateButtons: [ChatMessageTemplateButton]
    $footer: String
    $headerMediaBase64: String
    $headerMediaFilename: String
    $headerMediaMimeType: String
    $headline: String
    $name: String!
    $text: String!
    $type: String!
  ) {
    insertChatMessageTemplate(
      chatMessageTemplateButtons: $chatMessageTemplateButtons
      footer: $footer
      headerMediaBase64: $headerMediaBase64
      headerMediaFilename: $headerMediaFilename
      headerMediaMimeType: $headerMediaMimeType
      headline: $headline
      name: $name
      text: $text
      type_: $type
    ){chatMessageTemplateId}
  }
`

const DISTINCT_CHAT_MESSAGE_TEMPLATE_LANGUAGES_QUERY = gql`
  query{
    chat_message_templates(
      distinct_on: language
      where: {language: {_is_null: false}}
    ){language}
  }
`

export {
  ChatMessageTemplateCreate,
  ChatMessageTemplateShow,
  ChatMessageTemplatesList,
}
